import React, { useEffect, useRef, lazy } from 'react';
interface Photo {
  url: string;
  alt: string;
}
interface CurvedPhotoGalleryProps {
  photos: Photo[];
  title?: string;
  subtitle?: string;
}
export function CurvedPhotoGallery({
  photos,
  title,
  subtitle
}: CurvedPhotoGalleryProps) {
  const sliderRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider) return;
    let animationId: number;
    let position = 0;
    const speed = 0.5; // pixels per frame
    const animate = () => {
      position -= speed;
      const imageWidth = 320; // width + gap
      const totalWidth = imageWidth * photos.length;
      // Reset position when we've scrolled through all images
      if (Math.abs(position) >= totalWidth) {
        position = 0;
      }
      slider.style.transform = `translateX(${position}px)`;
      animationId = requestAnimationFrame(animate);
    };
    animationId = requestAnimationFrame(animate);
    // Pause animation on hover
    const handleMouseEnter = () => cancelAnimationFrame(animationId);
    const handleMouseLeave = () => {
      animationId = requestAnimationFrame(animate);
    };
    slider.addEventListener('mouseenter', handleMouseEnter);
    slider.addEventListener('mouseleave', handleMouseLeave);
    return () => {
      cancelAnimationFrame(animationId);
      slider.removeEventListener('mouseenter', handleMouseEnter);
      slider.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [photos.length]);
  // Triple the images for seamless infinite scroll
  const triplePhotos = [...photos, ...photos, ...photos];
  return <section className="relative py-24 md:py-32 overflow-hidden bg-gradient-to-b from-white via-gray-50 to-white" data-id="element-102">
      <div className="container mx-auto px-6 lg:px-8" data-id="element-103">
        {title && <div className="text-center mb-12 md:mb-20" data-id="element-104">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 md:mb-6" data-id="element-105">
              {title}
            </h2>
            {subtitle && <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-106">
                {subtitle}
              </p>}
          </div>}

        {/* Horizontal Continuous Slider */}
        <div className="relative" data-id="element-107">
          {/* Gradient overlays for fade effect */}
          <div className="absolute left-0 top-0 bottom-0 w-20 md:w-32 bg-gradient-to-r from-gray-50 to-transparent z-10 pointer-events-none" data-id="element-108" />
          <div className="absolute right-0 top-0 bottom-0 w-20 md:w-32 bg-gradient-to-l from-gray-50 to-transparent z-10 pointer-events-none" data-id="element-109" />

          <div className="overflow-hidden" data-id="element-110">
            <div ref={sliderRef} className="flex gap-4 md:gap-6" style={{
            width: 'fit-content'
          }} data-id="element-111">
              {triplePhotos.map((photo, index) => <div key={index} className="flex-shrink-0 relative overflow-hidden rounded-xl md:rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 group cursor-pointer" style={{
              width: '280px',
              height: '360px'
            }} data-id="element-112">
                  <img src={photo.url} alt={photo.alt} loading="lazy" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" style={{
                objectFit: 'cover'
              }} data-id="element-113" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-114" />
                </div>)}
            </div>
          </div>
        </div>

        {/* Optional: Add a subtle hint text */}
        <div className="text-center mt-8 md:mt-12" data-id="element-115">
          <p className="text-sm text-gray-500 italic" data-id="element-116">
            Hover to pause • Continuous scroll
          </p>
        </div>
      </div>
    </section>;
}